import React, { useCallback, useEffect, useRef, useState } from 'react';
import { callHandlerJson, IframeBridge } from '@alga-psa/extension-iframe-sdk';
import {
  Alert,
  Button,
  Checkbox,
  CustomSelect,
  Dialog,
  Input,
  LoadingIndicator,
  RadioGroup,
  Stack,
  Text,
  TextArea,
} from '@alga-psa/ui-kit';
import type {
  ApplianceDetail,
  BillingInfo,
  DetailAppliance,
  Envelope,
  TriggerResponse,
  WorkflowStatus,
} from './types';
import { Field, SecretBlock, dateInputToUnix, fmtUnixDate, fmtUnixDateTime, toDateInput } from './shared';

// ── Workflow runner: POST a trigger, then poll /workflows/:id to a terminal state ──

export type RunPhase = 'idle' | 'starting' | 'running' | 'completed' | 'failed';

export interface RunState {
  phase: RunPhase;
  workflowId?: string;
  auditLogId?: string | null;
  result?: unknown;
  error?: string;
}

const POLL_MS = 2000;
const POLL_MAX = 150; // ~5 minutes; the workflow keeps running, the dialog just stops waiting

export function useWorkflowRunner(bridge: IframeBridge) {
  const [state, setState] = useState<RunState>({ phase: 'idle' });
  const runRef = useRef(0);
  useEffect(() => () => { runRef.current++; }, []); // cancel polling on unmount

  const run = useCallback(async (path: string, body: unknown) => {
    const run = ++runRef.current;
    setState({ phase: 'starting' });
    try {
      const res = await callHandlerJson<Envelope<TriggerResponse>>(bridge, path, { method: 'POST', body });
      if (runRef.current !== run) return;
      if (!res?.success || !res.data?.workflow_id) throw new Error(res?.error || 'Failed to start the action');
      const workflowId = res.data.workflow_id;
      setState({ phase: 'running', workflowId, auditLogId: res.data.audit_log_id });

      for (let i = 0; i < POLL_MAX; i++) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        if (runRef.current !== run) return;
        const st = await callHandlerJson<Envelope<WorkflowStatus>>(bridge, `/workflows/${encodeURIComponent(workflowId)}`, { method: 'GET' });
        if (runRef.current !== run) return;
        if (!st?.success) throw new Error(st?.error || 'Failed to read workflow status');
        if (st.data.state === 'completed') { setState({ phase: 'completed', workflowId, result: st.data.result, auditLogId: st.data.audit_log_id }); return; }
        if (st.data.state === 'failed') { setState({ phase: 'failed', workflowId, error: st.data.error || 'The action failed', auditLogId: st.data.audit_log_id }); return; }
      }
      setState({ phase: 'failed', workflowId, error: 'Timed out waiting for the workflow. It may still complete; check the Audit tab.' });
    } catch (e) {
      if (runRef.current !== run) return;
      setState({ phase: 'failed', error: e instanceof Error ? e.message : String(e) });
    }
  }, [bridge]);

  const reset = useCallback(() => { runRef.current++; setState({ phase: 'idle' }); }, []);
  return { state, run, reset };
}

// ── Generic action dialog shell ──────────────────────────────────────────────

interface ActionDialogProps {
  isOpen: boolean;
  title: string;
  intro?: React.ReactNode;
  submitLabel: string;
  submitDisabled?: boolean;
  danger?: boolean;
  runner: ReturnType<typeof useWorkflowRunner>;
  onSubmit: () => void;
  /** Closed without completing (or after completing, once the user is done). */
  onClose: () => void;
  /** Completed successfully and the user dismissed the result — refresh data. */
  onDone: () => void;
  renderResult: (result: unknown) => React.ReactNode;
  children?: React.ReactNode;
}

export function ActionDialog(p: ActionDialogProps) {
  const { state } = p.runner;
  const busy = state.phase === 'starting' || state.phase === 'running';
  const close = () => { p.runner.reset(); p.onClose(); };
  const done = () => { p.runner.reset(); p.onDone(); p.onClose(); };
  return (
    <Dialog isOpen={p.isOpen} onClose={busy ? () => undefined : (state.phase === 'completed' ? done : close)} title={p.title}>
      <div style={{ minWidth: 420, maxWidth: 560 }}>
        <Stack gap={14}>
          {p.intro && state.phase === 'idle' && <Text size="sm" tone="muted">{p.intro}</Text>}

          {state.phase === 'idle' && (
            <>
              {p.children}
              <Stack direction="row" gap={8} justify="flex-end">
                <Button variant="outline" onClick={close}>Cancel</Button>
                <Button variant={p.danger ? 'destructive' : 'primary'} disabled={p.submitDisabled} onClick={p.onSubmit}>{p.submitLabel}</Button>
              </Stack>
            </>
          )}

          {busy && (
            <Stack gap={8} align="center" style={{ padding: '16px 0' }}>
              <LoadingIndicator text={state.phase === 'starting' ? 'Starting…' : 'Running…'} />
              {state.workflowId && <Text size="xs" tone="muted"><code>{state.workflowId}</code></Text>}
            </Stack>
          )}

          {state.phase === 'completed' && (
            <>
              <Alert tone="success"><Text size="sm">Done.</Text></Alert>
              {p.renderResult(state.result)}
              <Stack direction="row" gap={8} justify="flex-end">
                <Button variant="primary" onClick={done}>Close</Button>
              </Stack>
            </>
          )}

          {state.phase === 'failed' && (
            <>
              <Alert tone="danger"><Text size="sm">{state.error}</Text></Alert>
              {state.workflowId && <Text size="xs" tone="muted">Workflow <code>{state.workflowId}</code></Text>}
              <Stack direction="row" gap={8} justify="flex-end">
                <Button variant="outline" onClick={close}>Close</Button>
                <Button variant="primary" onClick={() => p.runner.reset()}>Try again</Button>
              </Stack>
            </>
          )}
        </Stack>
      </div>
    </Dialog>
  );
}

// ── Result renderers ─────────────────────────────────────────────────────────

type R = Record<string, unknown>;
const asRecord = (r: unknown): R => (r && typeof r === 'object' ? (r as R) : {});
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

function EmailNote({ result }: { result: R }) {
  if (result.email_sent === false) {
    return <Alert tone="warning"><Text size="sm">The email was not sent. Deliver the value above to the customer by hand.</Text></Alert>;
  }
  if (result.email_sent === true) return <Text size="xs" tone="muted">Emailed to the tenant contact.</Text>;
  return null;
}

export function InstallCodeResult({ result }: { result: unknown }) {
  const r = asRecord(result);
  return (
    <Stack gap={10}>
      {str(r.install_code) && <SecretBlock label="Install code" value={str(r.install_code)} />}
      {num(r.expires_at) != null && <Text size="sm" tone="muted">Valid until {fmtUnixDateTime(num(r.expires_at))}. Single-use.</Text>}
      {str(r.download_url) && <Text size="sm"><a href={str(r.download_url)} target="_blank" rel="noreferrer">ISO download link ↗</a></Text>}
      <EmailNote result={r} />
    </Stack>
  );
}

export function ActivationCodeResult({ result }: { result: unknown }) {
  const r = asRecord(result);
  return (
    <Stack gap={10}>
      {str(r.code) && <SecretBlock label="Activation code" value={str(r.code)} />}
      {num(r.expires_at) != null && <Text size="sm" tone="muted">Valid until {fmtUnixDateTime(num(r.expires_at))}. Entered in Settings → License.</Text>}
      <EmailNote result={r} />
    </Stack>
  );
}

export function LicenseKeyResult({ result, label }: { result: unknown; label: string }) {
  const r = asRecord(result);
  return (
    <Stack gap={10}>
      {str(r.jwt) && <SecretBlock label={label} value={str(r.jwt)} multiline />}
      {num(r.exp) != null && <Text size="sm" tone="muted">Expires {fmtUnixDateTime(num(r.exp))}. Pasted in Settings → License.</Text>}
      {str(r.license_sub) && <Text size="xs" tone="muted">License id <code>{str(r.license_sub)}</code></Text>}
      <EmailNote result={r} />
    </Stack>
  );
}

export function PlainResult({ result, fallback }: { result: unknown; fallback: string }) {
  const r = asRecord(result);
  const keys = Object.keys(r).filter((k) => typeof r[k] !== 'object');
  if (keys.length === 0) return <Text size="sm">{fallback}</Text>;
  return (
    <Stack gap={2}>
      {keys.map((k) => <Text key={k} size="sm"><span style={{ color: 'var(--alga-muted-fg)' }}>{k}:</span> {String(r[k])}</Text>)}
    </Stack>
  );
}

// ── Form primitives ──────────────────────────────────────────────────────────

function ReasonField({ value, onChange, required }: { value: string; onChange: (v: string) => void; required?: boolean }) {
  return (
    <Field label="Reason" required={required} hint="Recorded in the audit log.">
      <TextArea value={value} onChange={(e) => onChange(e.target.value)} rows={2} placeholder="Why this action is being taken" />
    </Field>
  );
}

function TypedConfirm({ expected, value, onChange }: { expected: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={`Type the company name to confirm`} required hint={expected}>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={expected} error={value.length > 0 && value !== expected} />
    </Field>
  );
}

interface DialogBase {
  bridge: IframeBridge;
  detail: ApplianceDetail;
  isOpen: boolean;
  onClose: () => void;
  onDone: () => void;
}

const tenantPath = (d: ApplianceDetail, suffix: string) => `/installs/${encodeURIComponent(d.tenant.tenant_id)}${suffix}`;

// ── Dialogs ──────────────────────────────────────────────────────────────────

export function ReissueInstallCodeDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const [reason, setReason] = useState('');
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Reissue install code" submitLabel="Reissue code"
      intro="Mints a fresh 30-day install code for the setup wizard and revokes any unclaimed one. Codes are single-use, so this is also how a lost email is resent."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/reissue-install-code'), { reason: reason || undefined })}
      renderResult={(r) => <InstallCodeResult result={r} />}
    >
      <ReasonField value={reason} onChange={setReason} />
    </ActionDialog>
  );
}

export function ReissueActivationCodeDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const [reason, setReason] = useState('');
  const connected = p.detail.appliances.filter((a) => !a.credential_revoked).length;
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Reissue activation code" submitLabel="Reissue activation code" danger={connected > 0}
      submitDisabled={!reason.trim()}
      intro="Mints a 1-day activation code the customer enters in Settings → License to connect this appliance to its paid entitlement."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/reissue-activation-code'), { reason })}
      renderResult={(r) => <ActivationCodeResult result={r} />}
    >
      {connected > 0 && (
        <Alert tone="warning">
          <Text size="sm">{connected} connected appliance{connected === 1 ? '' : 's'} will lose {connected === 1 ? 'its' : 'their'} credential and stop refreshing until the new code is redeemed.</Text>
        </Alert>
      )}
      <ReasonField value={reason} onChange={setReason} required />
    </ActionDialog>
  );
}

export function AirgapKeyDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const [reason, setReason] = useState('');
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Re-sign air-gap key" submitLabel="Re-sign key"
      intro="Signs a fresh 365-day offline license key bound to this tenant. The customer pastes it in Settings → License. Prior keys keep working until they expire."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/airgap-key'), { reason: reason || undefined })}
      renderResult={(r) => <LicenseKeyResult result={r} label="License key" />}
    >
      <ReasonField value={reason} onChange={setReason} />
    </ActionDialog>
  );
}

const DAY = 86400000;

export function ExtendProDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const [endDate, setEndDate] = useState(toDateInput(new Date(Date.now() + 14 * DAY)));
  const [seats, setSeats] = useState('');
  const [reason, setReason] = useState('');
  const min = toDateInput(new Date(Date.now() + DAY));
  const max = toDateInput(new Date(Date.now() + 90 * DAY));
  const endsAt = dateInputToUnix(endDate);
  const seatsNum = seats.trim() ? parseInt(seats, 10) : undefined;
  const seatsBad = seats.trim() !== '' && (!Number.isInteger(seatsNum) || (seatsNum as number) < 1);
  const valid = !!endsAt && endsAt > Date.now() / 1000 && endDate <= max && !seatsBad && reason.trim().length > 0;
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Extend Pro" submitLabel="Grant Pro until date" submitDisabled={!valid}
      intro="Issues a time-boxed Pro license key with no subscription. The customer pastes it in Settings → License and drops back to Essentials on the end date. Stripe is not involved. Pick the total Pro runway: the appliance's local trial days are masked once a key is installed."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/extend-pro'), { ends_at: endsAt, seats: seatsNum, reason })}
      renderResult={(r) => <LicenseKeyResult result={r} label="Pro license key" />}
    >
      <Field label="Pro until" required hint="Default 14 days, maximum 90 days from today.">
        <Input type="date" value={endDate} min={min} max={max} onChange={(e) => setEndDate(e.target.value)} />
      </Field>
      <Field label="Seats" hint="Leave blank for unlimited.">
        <Input type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} placeholder="unlimited" error={seatsBad} />
      </Field>
      <ReasonField value={reason} onChange={setReason} required />
    </ActionDialog>
  );
}

export function ChangeEntitlementDialog(p: DialogBase & { billing: BillingInfo | null }) {
  const runner = useWorkflowRunner(p.bridge);
  const ent = p.detail.entitlement;
  const [mode, setMode] = useState<'billed' | 'comp'>('billed');
  const [seats, setSeats] = useState(ent?.seats != null ? String(ent.seats) : '');
  const [proration, setProration] = useState<'create_prorations' | 'none'>('create_prorations');
  const [reason, setReason] = useState('');
  const seatsNum = seats.trim() ? parseInt(seats, 10) : undefined;
  const seatsBad = seats.trim() !== '' && (!Number.isInteger(seatsNum) || (seatsNum as number) < 1);
  const seatsChanged = (seatsNum ?? null) !== (ent?.seats ?? null);
  const valid = !seatsBad && seatsChanged && reason.trim().length > 0;
  const stripeSeats = p.billing?.billing?.seat_quantity ?? null;
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Change seats" submitLabel={mode === 'billed' ? 'Update Stripe and license' : 'Update license only'} submitDisabled={!valid}
      intro="The appliance picks the change up on its next daily check-in, or immediately with “Refresh license now” on its License page."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/entitlement'), {
        mode, seats: seatsNum ?? null,
        proration: mode === 'billed' ? proration : undefined, reason,
      })}
      renderResult={(r) => <PlainResult result={r} fallback="Entitlement updated." />}
    >
      <Field label="Mode" required>
        <RadioGroup
          value={mode}
          onChange={(v) => setMode(v as 'billed' | 'comp')}
          options={[
            { value: 'billed', label: 'Billed — update the Stripe subscription first, then the license' },
            { value: 'comp', label: 'Comp — update the license only; Stripe stays as it is' },
          ]}
        />
      </Field>
      <Field label="Seats" hint="Blank = unlimited.">
        <Input type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} placeholder="unlimited" error={seatsBad} />
      </Field>
      {mode === 'billed' && (
        <>
          <Field label="Proration">
            <RadioGroup
              value={proration}
              onChange={(v) => setProration(v as 'create_prorations' | 'none')}
              options={[
                { value: 'create_prorations', label: 'Prorate (Stripe default)' },
                { value: 'none', label: 'No proration — takes effect next period' },
              ]}
            />
          </Field>
          <Alert tone="info">
            <Text size="sm">
              Stripe preview: seats {stripeSeats ?? '?'} → {seatsNum ?? 'unlimited'}
              {p.billing?.billing ? ` on ${p.billing.billing.subscription_id} (${p.billing.billing.status})` : ' (subscription not loaded)'}.
            </Text>
          </Alert>
        </>
      )}
      {mode === 'comp' && (
        <Alert tone="warning">
          <Text size="sm">
            After this, Stripe bills {stripeSeats ?? '?'} seat{stripeSeats === 1 ? '' : 's'} while the license allows {seatsNum ?? 'unlimited'}. The Billing card will show the mismatch.
          </Text>
        </Alert>
      )}
      <ReasonField value={reason} onChange={setReason} required />
    </ActionDialog>
  );
}

export function PauseBillingDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const [resumeDate, setResumeDate] = useState('');
  const [behavior, setBehavior] = useState<'void' | 'keep_as_draft' | 'mark_uncollectible'>('void');
  const [reason, setReason] = useState('');
  const resumesAt = dateInputToUnix(resumeDate);
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Pause billing" submitLabel="Pause collection" submitDisabled={!reason.trim() || (resumeDate !== '' && !resumesAt)}
      intro="Stripe keeps the subscription active and the license keeps rolling, but no payment is collected until collection resumes."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/pause-billing'), { resumes_at: resumesAt ?? undefined, behavior, reason })}
      renderResult={(r) => <PlainResult result={r} fallback="Collection paused." />}
    >
      <Field label="Resume on" hint="Leave blank to pause until resumed by hand.">
        <Input type="date" value={resumeDate} min={toDateInput(new Date(Date.now() + DAY))} onChange={(e) => setResumeDate(e.target.value)} />
      </Field>
      <Field label="Invoices during the pause" required>
        <RadioGroup
          value={behavior}
          onChange={(v) => setBehavior(v as typeof behavior)}
          options={[
            { value: 'void', label: 'Void — no record of the charge' },
            { value: 'keep_as_draft', label: 'Keep as draft — collect later by hand' },
            { value: 'mark_uncollectible', label: 'Mark uncollectible' },
          ]}
        />
      </Field>
      <ReasonField value={reason} onChange={setReason} required />
    </ActionDialog>
  );
}

export function ResumeBillingDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const [reason, setReason] = useState('');
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Resume billing" submitLabel="Resume collection"
      intro="Clears the pause on the Stripe subscription. Draft invoices kept during the pause still need collection turned on by hand in Stripe."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/resume-billing'), { reason: reason || undefined })}
      renderResult={(r) => <PlainResult result={r} fallback="Collection resumed." />}
    >
      <ReasonField value={reason} onChange={setReason} />
    </ActionDialog>
  );
}

export function SetStatusDialog(p: DialogBase & { target: 'suspended' | 'active' }) {
  const runner = useWorkflowRunner(p.bridge);
  const [reason, setReason] = useState('');
  const suspend = p.target === 'suspended';
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title={suspend ? 'Suspend tenant' : 'Reactivate tenant'} submitLabel={suspend ? 'Suspend' : 'Reactivate'} danger={suspend}
      submitDisabled={!reason.trim()}
      intro={suspend
        ? 'Soft and reversible. The next check-in stops issuing fresh tokens; the appliance keeps its current token and drops to Essentials when it expires, up to 31 days later. Console actions are blocked while suspended.'
        : 'Clears the suspension. The next check-in re-signs the license, or the customer presses “Refresh license now”.'}
      onSubmit={() => void runner.run(tenantPath(p.detail, '/status'), { status: p.target, reason })}
      renderResult={(r) => <PlainResult result={r} fallback="Status updated." />}
    >
      <ReasonField value={reason} onChange={setReason} required />
    </ActionDialog>
  );
}

export function RevokeDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [hard, setHard] = useState(false);
  const name = p.detail.tenant.company_name;
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Revoke entitlement" submitLabel="Revoke" danger submitDisabled={typed !== name || !reason.trim()}
      intro="Soft-revokes the paid entitlement. Check-in stops renewing and the appliance drops to Essentials when its current token expires (up to 31 days). The tenant record stays as it is."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/revoke'), { reason, hard })}
      renderResult={(r) => <PlainResult result={r} fallback="Entitlement revoked." />}
    >
      <Checkbox
        label="Also revoke appliance credentials now (hard). The box is cut off at its next check-in instead of at token expiry, and needs a new activation code to reconnect."
        checked={hard}
        onChange={(e) => setHard(e.target.checked)}
      />
      <ReasonField value={reason} onChange={setReason} required />
      <TypedConfirm expected={name} value={typed} onChange={setTyped} />
    </ActionDialog>
  );
}

export function CancelTenantDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const name = p.detail.tenant.company_name;
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Cancel tenant" submitLabel="Cancel tenant" danger submitDisabled={typed !== name || !reason.trim()}
      intro="Sets the tenant to cancelled and revokes its active entitlement. Registry data is kept for the record; nothing is deleted."
      onSubmit={() => void runner.run(tenantPath(p.detail, '/status'), { status: 'cancelled', reason })}
      renderResult={(r) => <PlainResult result={r} fallback="Tenant cancelled." />}
    >
      <ReasonField value={reason} onChange={setReason} required />
      <TypedConfirm expected={name} value={typed} onChange={setTyped} />
    </ActionDialog>
  );
}

export function RevokeApplianceDialog(p: DialogBase & { appliance: DetailAppliance | null }) {
  const runner = useWorkflowRunner(p.bridge);
  const [reason, setReason] = useState('');
  const a = p.appliance;
  return (
    <ActionDialog
      isOpen={p.isOpen && !!a} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Revoke appliance credential" submitLabel="Revoke credential" danger submitDisabled={!reason.trim()}
      intro={`Hard revoke of ${a?.appliance_id ?? ''}. Its next check-in is rejected and it stops refreshing; the entitlement itself is untouched. Reconnect with a new activation code.`}
      onSubmit={() => a && void runner.run(tenantPath(p.detail, `/appliances/${encodeURIComponent(a.appliance_id)}/revoke`), { reason })}
      renderResult={(r) => <PlainResult result={r} fallback="Credential revoked." />}
    >
      <ReasonField value={reason} onChange={setReason} required />
    </ActionDialog>
  );
}

export function EditTenantDialog(p: DialogBase) {
  const runner = useWorkflowRunner(p.bridge);
  const t = p.detail.tenant;
  const [company, setCompany] = useState(t.company_name);
  const [contactName, setContactName] = useState(t.contact_name ?? '');
  const [email, setEmail] = useState(t.contact_email);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const changed = company !== t.company_name || (contactName || null) !== t.contact_name || email !== t.contact_email;
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Edit tenant" submitLabel="Save" submitDisabled={!changed || !company.trim() || !emailOk}
      intro="Changing the contact email also changes who can sign in to the licensing portal for this tenant."
      onSubmit={() => void runner.run(tenantPath(p.detail, ''), {
        __method: 'PATCH',
        company_name: company !== t.company_name ? company.trim() : undefined,
        contact_name: (contactName || null) !== t.contact_name ? (contactName.trim() || null) : undefined,
        contact_email: email !== t.contact_email ? email.trim() : undefined,
      })}
      renderResult={() => <Text size="sm">Tenant updated.</Text>}
    >
      <Field label="Company" required><Input value={company} onChange={(e) => setCompany(e.target.value)} /></Field>
      <Field label="Contact name"><Input value={contactName} onChange={(e) => setContactName(e.target.value)} /></Field>
      <Field label="Contact email" required><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={!emailOk} /></Field>
    </ActionDialog>
  );
}

export function CreateTenantDialog(p: { bridge: IframeBridge; isOpen: boolean; onClose: () => void; onDone: () => void }) {
  const runner = useWorkflowRunner(p.bridge);
  const [company, setCompany] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [edition, setEdition] = useState<string>('essentials');
  const [product, setProduct] = useState<string>('psa');
  const [seats, setSeats] = useState('');
  const [subId, setSubId] = useState('');
  const [comp, setComp] = useState(false);
  const [compEnd, setCompEnd] = useState(toDateInput(new Date(Date.now() + 14 * DAY)));
  const [note, setNote] = useState('');
  const paid = edition !== 'essentials';
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const seatsNum = seats.trim() ? parseInt(seats, 10) : undefined;
  const seatsBad = seats.trim() !== '' && (!Number.isInteger(seatsNum) || (seatsNum as number) < 1);
  const compEndsAt = dateInputToUnix(compEnd);
  const paidOk = !paid || (comp ? !!compEndsAt && note.trim().length > 0 : /^sub_/.test(subId.trim()));
  const valid = company.trim().length > 0 && emailOk && !seatsBad && paidOk;
  return (
    <ActionDialog
      isOpen={p.isOpen} onClose={p.onClose} onDone={p.onDone} runner={runner}
      title="Create appliance tenant" submitLabel="Create and email install code" submitDisabled={!valid}
      intro="Mints a registry tenant and a 30-day install code, and emails the code with the ISO link to the contact."
      onSubmit={() => void runner.run('/installs', {
        company_name: company.trim(), contact_name: contactName.trim() || undefined, contact_email: email.trim(),
        edition, product_code: product, seats: seatsNum,
        stripe_sub_id: paid && !comp ? subId.trim() : undefined,
        comp: paid && comp ? { ends_at: compEndsAt, note: note.trim() } : undefined,
      })}
      renderResult={(r) => (
        <Stack gap={8}>
          {str(asRecord(r).tenant_id) && <Text size="sm">Tenant <code>{str(asRecord(r).tenant_id)}</code></Text>}
          <InstallCodeResult result={r} />
        </Stack>
      )}
    >
      <Field label="Company" required><Input value={company} onChange={(e) => setCompany(e.target.value)} /></Field>
      <Stack direction="row" gap={12} style={{ flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}><Field label="Contact name"><Input value={contactName} onChange={(e) => setContactName(e.target.value)} /></Field></div>
        <div style={{ flex: 1, minWidth: 180 }}><Field label="Contact email" required><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={email.length > 0 && !emailOk} /></Field></div>
      </Stack>
      <Stack direction="row" gap={12} style={{ flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <Field label="Edition" required>
            <CustomSelect value={edition} onValueChange={setEdition} options={[{ value: 'essentials', label: 'essentials' }, { value: 'pro', label: 'pro' }]} />
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <Field label="Product" required>
            <CustomSelect value={product} onValueChange={setProduct} options={[{ value: 'psa', label: 'psa' }, { value: 'algadesk', label: 'algadesk' }]} />
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <Field label="Seats" hint="Blank = unlimited."><Input type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} disabled={!paid} error={seatsBad} /></Field>
        </div>
      </Stack>
      {paid && (
        <>
          <Checkbox label="Comp — no Stripe subscription; time-boxed Pro" checked={comp} onChange={(e) => setComp(e.target.checked)} />
          {!comp ? (
            <Field label="Stripe subscription id" required hint="Anchors the paid entitlement, e.g. sub_1AbC…">
              <Input value={subId} onChange={(e) => setSubId(e.target.value)} placeholder="sub_…" error={subId.length > 0 && !/^sub_/.test(subId.trim())} />
            </Field>
          ) : (
            <>
              <Field label="Pro until" required>
                <Input type="date" value={compEnd} min={toDateInput(new Date(Date.now() + DAY))} max={toDateInput(new Date(Date.now() + 90 * DAY))} onChange={(e) => setCompEnd(e.target.value)} />
              </Field>
              <Field label="Note" required hint="Why this tenant gets Pro without billing.">
                <TextArea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
              </Field>
              <Text size="xs" tone="muted">Ends {fmtUnixDate(compEndsAt)}.</Text>
            </>
          )}
        </>
      )}
    </ActionDialog>
  );
}
